import { combineReducers, applyMiddleware, createStore, compose } from 'redux';
import thunk from 'redux-thunk';
import serverVars from 'server-vars';
import { clientIntlPolyfill } from 'dibs-intl/exports/clientIntlPolyfill';

import { getBuyerHeaderClient } from './header';
import { getBuyerFooterClient } from './footer';
import { getBuyerCookieNotificationClient } from './cookieNotification';
import initializeTracking from '../utils/initializeTracking';
import { addLoginHandlers } from '../utils/loginHandlers';
import dblReducers from '../reducers';
import NotificationQueue from '../notificationQueue/Queue';
import createRelayEnvironment from '../relayClientEnvironment';
import AuthModalCheck from '../utils/AuthModalCheck';
import isPageType from '../utils/isPageType';
import abExperiments from '../utils/abExperiments';
import pageTypes from '../utils/pageConstants';
import scriptInjector from '../utils/scriptInjector';
import chatUtils from '../utils/chat';
import contactModal from '../contactModal/async';
import '../scss/dibs.scss';

import { isMasqueradingAsBuyer, cookieJarConstants } from 'dibs-cookie-jar';

import Cookies from 'js-cookie';

const { USER, MASQUERADE_BUYER, USER_EMAIL, USER_TYPE } = cookieJarConstants;

const dblMiddleware = [thunk];

// will be used to store the queue so that functions exported from dbl can interact with it
let queue;

function createGlobalStore(reducers, middlewares, state) {
    const reducer = combineReducers({ ...reducers, ...dblReducers });
    const middleware = middlewares
        .concat(dblMiddleware)
        .filter((value, index, self) => self.indexOf(value) === index);

    let enhancer;
    if (process.env.NODE_ENV !== 'production') {
        const { composeWithDevTools } = require('redux-devtools-extension');
        enhancer = composeWithDevTools;
    } else {
        enhancer = compose;
    }

    return createStore(reducer, state, enhancer(applyMiddleware(...middleware)));
}

Cookies.defaults = {
    domain: serverVars.get('cookieDomain'),
    expires: 182,
    path: '/',
};

function logoutFunc() {
    if (isMasqueradingAsBuyer(document.cookie)) {
        Cookies.remove(MASQUERADE_BUYER);
    } else {
        Cookies.remove(USER);
        Cookies.remove(USER_EMAIL);
        Cookies.remove(USER_TYPE);
    }
    location.reload();
}

/**
 * @param {object} options
 */
function _initialize(options = {}) {
    const {
        autoRunNotificationQueue = true,
        reducers = {},
        middleware = [],
        logoutUser = logoutFunc,
        notifications = [],
        state = {},
    } = options;

    if (
        state.hasOwnProperty('cart') ||
        state.hasOwnProperty('header') ||
        state.hasOwnProperty('footer')
    ) {
        throw new Error(`state cannot include reserved keys: cart, header, footer`);
    }
    const data = JSON.parse(serverVars.get('headerRelayPreloadedData'));
    const relayEnv = createRelayEnvironment(data);
    const { header, cart } = serverVars.get('header') || {};
    const { footer } = serverVars.get('footer') || {};
    header.logoutUser = logoutUser;

    const store = createGlobalStore(reducers, middleware, { header, footer, cart, ...state });
    getBuyerHeaderClient({
        node: document.getElementById('js-header'),
        store,
        relayEnv,
    });
    getBuyerFooterClient({
        node: document.getElementById('js-footer'),
        store,
        relayEnv,
    });
    getBuyerCookieNotificationClient({
        node: document.getElementById('js-cookie-notification'),
        relayEnv,
    });

    AuthModalCheck.init();

    queue = new NotificationQueue(notifications, store, relayEnv, autoRunNotificationQueue);

    chatUtils.start();
    return store;
}

/**
 * @deprecated Use initializeDBL
 * @param {object} options
 */
function initialize(options = {}) {
    console.warn('DEPRECATED: dibs-buyer-layout `initialize`. Use `initializeDBL` instead.');
    return _initialize(options);
}

function initializeDBL(options = {}) {
    const { initializers = [], ...otherOptions } = options;

    const localeDataKey = serverVars.get('localeDataKey') || 'en';
    initializers.push(clientIntlPolyfill(localeDataKey));

    return Promise.all(initializers).then(values => {
        const store = _initialize(otherOptions);
        const publicQueue = {
            _getNotificationQueue() {
                return queue._getNotificationQueue();
            },
            runNotificationQueue() {
                queue.runQueue();
            },
        };
        return { values, store, queue: publicQueue };
    });
}

function _getNotificationQueue() {
    return queue._getNotificationQueue();
}

function runNotificationQueue() {
    queue.runQueue();
}

// used only for testing notification order
export {
    abExperiments,
    addLoginHandlers,
    contactModal,
    initializeTracking,
    initialize,
    initializeDBL,
    isPageType,
    pageTypes,
    scriptInjector,
    _getNotificationQueue,
    runNotificationQueue,
};
